import { useEffect, useState } from 'react';
import { TRACKER_IDS, TRACKER_NAMES, type Payload, type WindowKey } from '../api/types';
import { ago, usd } from '../lib/format';
import { finite } from '../ui/charts';
import { Emblem } from '../ui/compare';
import { CardHead } from '../ui/glass';

interface Status {
  running?: boolean; phase?: string; completedAt?: string | null; nextRunAt?: string | null;
  lastError?: string | null; sources?: { id: string; status: string; casinos: number; error?: string | null }[];
}

const time = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : null;

/* The four trackers as sources: what HouseGap reads from each, whether that
   read is fresh, and whether it counts toward a median. The notes come from
   the collector itself, so they describe what was actually read this hour. */
export function Sources({ data, win }: { data: Payload; win: WindowKey }) {
  const [status, setStatus] = useState<Status | null>(null);

  useEffect(() => {
    let live = true;
    const read = () => fetch('/api/casinobias/status')
      .then((r) => (r.ok ? r.json() as Promise<Status> : Promise.reject(new Error(String(r.status)))))
      .then((s) => { if (live) setStatus(s); })
      .catch(() => { /* the panel simply stays quiet */ });
    read();
    const timer = setInterval(read, 30_000);
    return () => { live = false; clearInterval(timer); };
  }, []);

  const a = data.analysis.volume[win];
  const next = time(status?.nextRunAt);
  const last = status?.completedAt ?? data.scrapedAt;

  return (
    <div className="page">
      <section className="tile banner-head banner-sources">
        <div className="page-head">
          <div>
            <h1>Sources</h1>
            <p>Four public trackers, read every hour. What HouseGap reads from each, whether that read is fresh, and whether it counts toward a median.</p>
          </div>
          <span className="status-badge">{status?.running ? 'Collecting now' : next ? `Next collection ${next}` : 'Collects hourly'}</span>
        </div>
      </section>

      <div className="pgrid">
        {TRACKER_IDS.map((id) => {
          const src = data.sources.find((s) => s.id === id);
          const liveRow = status?.sources?.find((s) => s.id === id);
          const counted = !!a?.consensusSources.includes(id);
          const total = a?.totals?.[id]?.value;
          const casinos = liveRow?.casinos ?? a?.totals?.[id]?.casinos;
          const fresh = !!src?.ok && !src.stale;
          return (
            <section key={id} className={`tile span-3 srccard${counted ? '' : ' is-ref'}`}>
              <div className="tcard-head">
                <Emblem id={id} />
                <div>
                  <h2>{TRACKER_NAMES[id]}</h2>
                  <span className={`tcard-state${fresh ? '' : ' warn'}`}><i aria-hidden />{src?.ok ? (src.stale ? 'Stale feed' : 'Fresh, read hourly') : 'Failed last read'}</span>
                </div>
                <span className="tcard-role" style={{ ['--tc' as string]: `var(--tracker-${id})` }}>{counted ? 'Counted' : 'Reference'}</span>
              </div>
              <ul className="srccard-figs">
                <li><b>{finite(casinos) ? casinos : '—'}</b><small>casinos listed</small></li>
                <li><b>{finite(total) ? usd(total) : '—'}</b><small>{a?.label ?? win} deposits</small></li>
              </ul>
              {src?.note && (
                <div className="srccard-note">
                  <small>What HouseGap reads</small>
                  <p>{src.note}</p>
                </div>
              )}
              {src?.scope && <p className="tcard-note">{src.scope}</p>}
              {src?.error && <p className="tcard-note">Last read failed: {src.error}</p>}
              <div className="srccard-links">
                {src?.url && <a className="tile-note" href={src.url} target="_blank" rel="noreferrer">Visit {TRACKER_NAMES[id]} ↗</a>}
                <a className="tile-note" href={`/compare?w=${win}`}>Compare it →</a>
              </div>
            </section>
          );
        })}

        <section className="tile span-5">
          <CardHead icon="clock" tone="blue" title="The collection" note="Every hour the collector reads all four trackers, then this site redraws" />
          <ul className="kv srckv">
            <li><span>{time(last) ?? '—'}</span><span>last collected{last ? `, ${ago(last)}` : ''}</span></li>
            <li><span>{status?.running ? 'now' : next ?? '—'}</span><span>{status?.running ? `collecting${status.phase ? `: ${status.phase}` : ''}` : 'next collection'}</span></li>
            <li><span>{data.sources.filter((s) => s.ok && !s.stale).length} of {data.sources.length}</span><span>sources fresh</span></li>
          </ul>
          {status?.lastError && <div className="banner"><span>Last collection reported: {status.lastError}</span></div>}
        </section>

        <section className="tile span-7">
          <CardHead icon="info" tone="violet" title="How to read the figures" note="The rules every page on this site follows" />
          <ul className="check-list">
            <li><span><b>Median, not average.</b> The middle counted reading, so one tracker with an outlier cannot drag the figure.</span></li>
            <li><span><b>Apart</b> is the highest reading minus the lowest, over the median, as a percentage.</span></li>
            <li><span><b>Missing is not zero.</b> A tracker that does not list a casino is left out of its median rather than counted as nothing.</span></li>
            <li><span><b>A starred figure</b> is published but not counted, because that tracker is reference-only.</span></li>
          </ul>
        </section>
      </div>
    </div>
  );
}
